export const MULTIPART_MB_THRESHOLD = Number(localStorage.getItem('MULTIPART_MB_THRESHOLD') || 100);

// target size of multipart segments - matches the threshold bytes but could be resized independently
export const MULTIPART_PART_SIZE_BYTES = MULTIPART_MB_THRESHOLD * 1024 * 1024;

// how big the file has to be before it MUST be uploaded multipart
export const MULTIPART_THRESHOLD_BYTES = MULTIPART_MB_THRESHOLD * 1024 * 1024;

/**
 * The API signs part urls with a one hour expiry. Refresh a batch before it goes stale so a slow
 * connection working through 1GB of segments doesn't start failing partway through.
 */
export const URL_REFRESH_AFTER_MS = 50 * 60 * 1000;

/**
 * The API aborts an upload after an hour of inactivity. Requesting the next batch of urls counts as
 * activity, but a single batch can outlast an hour on a slow connection, so ping it directly too.
 */
export const KEEP_ALIVE_INTERVAL_MS = 10 * 60 * 1000;

export const MAX_CONCURRENT_PARTS = 3;
export const MAX_PART_ATTEMPTS = 3;

/**
 * How long a saved resume record is worth keeping. The API drops an upload after 8 hours of
 * inactivity (MULTIPART_UPLOAD_TIMEOUT_MINUTES) and every part goes with it, so a record older than
 * that describes an upload that no longer exists - resuming it would only earn a 410.
 *
 * Keep this in step with the API's window: too short and we throw away records for uploads that are
 * still resumable, too long and we offer resumes that can only fail.
 */
export const RESUME_RECORD_TTL_MS = 8 * 60 * 60 * 1000;

/**
 * How long a part may go without any upload progress before we treat it as stalled and start it
 * again. Suspending the network doesn't reliably fail the request - the socket can be left open and
 * silent, in which case nothing ever rejects and the upload sits there looking active forever.
 *
 * Has to be generous enough that a genuinely slow connection isn't mistaken for a stall. This is a
 * gap *between progress events*, not the time to upload a whole part.
 */
export const PART_STALL_TIMEOUT_MS = 60 * 1000;

/**
 * How long to wait for S3's response once a part's body has gone out. Progress events stop at that
 * point, so PART_STALL_TIMEOUT_MS can't cover this phase - and a connection suspended after the
 * last byte was sent leaves the request waiting on a reply that will never arrive.
 */
export const RESPONSE_STALL_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * How long we keep waiting out a network interruption with nothing completing before giving up on
 * the upload. A sleeping machine can suspend network IO for a long time, so this is deliberately
 * long - but it can't outlast the API's own inactivity timeout, which no amount of retrying
 * survives.
 */
export const MAX_STALLED_MS = 30 * 60 * 1000;

/** How often to re-check for a usable connection while the network is down. */
export const NETWORK_RETRY_INTERVAL_MS = 5 * 1000;

/**
 * navigator.onLine flips back to true as soon as the interface is up, which on a waking machine is
 * before any traffic actually flows. Settle for a moment on top of it rather than throwing an
 * attempt at a half open connection.
 */
export const NETWORK_SETTLE_MS = 2 * 1000;
